import { useEffect, useState } from "react";
import { Copy, Loader2, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { btnGold, errMsg, inp, lbl } from "./ui";

const ROLE = { attendance: "Attendance admin", projection: "Projection admin", presenter: "Presenter only" };

function RoleBoxes({ roles, onChange, testPrefix }) {
  const toggle = (r) => onChange(roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r]);
  return (
    <div className="flex gap-4 text-xs text-neutral-300">
      {Object.entries(ROLE).map(([k, l]) => (
        <label key={k} className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={roles.includes(k)} onChange={() => toggle(k)} className="accent-[#D4AF37]" data-testid={`${testPrefix}-${k}`} /> {l}
        </label>
      ))}
    </div>
  );
}

export default function TeamPanel() {
  const [members, setMembers] = useState(null);
  const [f, setF] = useState({ name: "", email: "", roles: ["projection"] });
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const load = () => api.get("/org/team").then((r) => setMembers(r.data)).catch((e) => toast.error(errMsg(e, "Could not load team")));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!f.roles.length) { toast.error("Choose at least one role"); return; }
    setBusy(true);
    try {
      const r = await api.post("/org/team", f);
      toast.success(r.data.email_sent ? `Invitation emailed to ${f.email}` : "Added — email could not be sent, share the link below");
      setLink(r.data.setup_link ? { email: f.email, url: r.data.setup_link } : null);
      setF({ name: "", email: "", roles: ["projection"] });
      load();
    } catch (err) { toast.error(errMsg(err, "Could not add team member")); } finally { setBusy(false); }
  };
  const setRoles = async (m, roles) => {
    if (!roles.length) { toast.error("Keep at least one role, or remove the person"); return; }
    try { setMembers((await api.put(`/org/team/${m.user_id}`, { roles })).data); toast.success("Roles updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email} from your church team?`)) return;
    try { setMembers((await api.delete(`/org/team/${m.user_id}`)).data); if (link?.email === m.email) setLink(null); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="space-y-5" data-testid="team-panel">
      <div>
        <h3 className="font-serif-display text-2xl text-white">Church team</h3>
        <p className="text-xs text-neutral-400 mt-1">Appoint separate administrators for HOH Attendance and HOH Hymn Projection, or give one person both roles. Each person only sees the application they manage.</p>
      </div>
      <form onSubmit={add} className="luxury-card rounded-xl p-4 grid sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Name</label><input className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="team-name" /></div>
        <div><label className={lbl}>Email</label><input type="email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="team-email" /></div>
        <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3">
          <RoleBoxes roles={f.roles} onChange={(roles) => setF({ ...f, roles })} testPrefix="team-role" />
          <button disabled={busy} className={btnGold} data-testid="team-add">{busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Add administrator</button>
        </div>
      </form>
      {link && (
        <div className="text-xs text-neutral-300 border border-[#D4AF37]/30 rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="team-setup-link">
          Set-password link for {link.email} (valid 7 days):
          <code className="text-[#D4AF37] break-all">{link.url}</code>
          <button type="button" onClick={() => { navigator.clipboard.writeText(link.url); toast.success("Link copied"); }} className="text-neutral-400 hover:text-white" data-testid="team-copy-link"><Copy size={13} /></button>
        </div>
      )}
      {!members ? <Loader2 className="animate-spin text-[#D4AF37]" /> : (
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.user_id} className="luxury-card rounded-lg p-3 flex flex-wrap items-center gap-4" data-testid={`team-member-${m.email}`}>
              <div className="flex-1 min-w-[180px]"><div className="text-white text-sm">{m.name || m.email}</div><div className="text-[11px] text-neutral-500">{m.email}</div></div>
              {m.is_owner ? <span className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Account owner · full access</span> : (
                <>
                  <RoleBoxes roles={m.roles} onChange={(roles) => setRoles(m, roles)} testPrefix={`team-${m.user_id}`} />
                  <button onClick={() => remove(m)} title="Remove" className="p-2 text-neutral-400 hover:text-red-400" data-testid={`team-remove-${m.user_id}`}><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
